import { useEffect, useState, type ReactNode } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import { HelpCircle, CheckCircle2, XCircle, Trophy } from "lucide-react"
import { savePendingAnswers, type PendingAnswer } from "@/lib/guest-quiz"

type Answer   = { id: number; content: string }
type Question = { id: number; content: string; answers: Answer[] }
type Stats    = {
  total_answered:  number
  total_correct:   number
  total_questions: number
  percentage:      number
}
type GuestStats = { total_questions: number }

type Props = {
  hero_image: string
  og_image:   string
  signed_in:  boolean
  completed:  boolean
  session_id: number | null
  questions:  Question[]
  stats:      Stats | GuestStats
}

type AnswerResult = {
  correct:           boolean
  correct_answer_id: number
  more_info:         string | null
  correct_rate:      number | null
  difficulty:        string | null
}

function csrf() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
}

// ── Guest: no questions available ───────────────────────────────────────────
function NoQuestionsYet() {
  return (
    <div className="max-w-xl mx-auto py-12 px-4 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-950/30 mb-6">
        <HelpCircle className="w-8 h-8 text-amber-500" />
      </div>
      <h1 className="mb-3">Australian Music Trivia</h1>
      <p className="text-stone-500 dark:text-stone-400 leading-relaxed">
        No trivia questions yet — check back soon!
      </p>
    </div>
  )
}

// ── Guest: intro screen ──────────────────────────────────────────────────────
function GuestIntro({ heroImage, stats, onStart }: { heroImage: string; stats: GuestStats; onStart: () => void }) {
  return (
    <div className="max-w-xl mx-auto py-6 px-4 text-center">
      {/* Decorative: the poster repeats the heading and tagline that follow it in
          real text, so it is hidden from assistive tech rather than described
          twice. width/height are set to reserve space and avoid layout shift. */}
      <img
        src={heroImage}
        alt=""
        aria-hidden="true"
        width={1400}
        height={793}
        className="mb-6 w-full h-auto rounded-xl border border-hairline shadow-sm"
      />
      <h1 className="mb-3">Australian Music Trivia</h1>
      <p className="text-stone-500 dark:text-stone-400 leading-relaxed mb-2">
        Test your knowledge across {stats.total_questions} questions covering bands, recordings, venues and chart history.
      </p>
      <p className="text-sm text-stone-400 dark:text-stone-500 mb-8">
        Jump straight in — no sign-up needed to play. Want to keep your score and climb the leaderboard?
        We'll ask you to create a quick free account once you're done. It's only there to remember your
        score — no spam, no dodgy marketing emails, nothing weird.
      </p>
      <Button size="lg" onClick={onStart}>Start the quiz</Button>
    </div>
  )
}

// ── Guest: round complete, prompt to save ───────────────────────────────────
function GuestQuizComplete({ stats, collected }: { stats: Stats; collected: PendingAnswer[] }) {
  useEffect(() => {
    savePendingAnswers(collected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="max-w-xl mx-auto py-12 px-4 text-center">
      <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-4" />
      <h1 className="mb-2">Nice work!</h1>
      <p className="text-stone-500 dark:text-stone-400 mb-6 leading-relaxed">
        You answered {collected.length} question{collected.length === 1 ? "" : "s"}.
      </p>
      <div className="rounded-xl border border-hairline bg-surface p-6 mb-8 text-left">
        <h2 className="text-base mb-4">Your score</h2>
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-ink-muted">Questions answered</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.total_answered}</dd>
          <dt className="text-ink-muted">Correct answers</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.total_correct}</dd>
          <dt className="text-ink-muted">Score</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.percentage}%</dd>
        </dl>
      </div>
      <p className="text-sm text-ink-muted mb-6">
        Register to keep your score on the leaderboard and play the rest of the quiz — it's free, and we'll
        never use it for spam.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link href="/signup?return_to=/trivia"><Button size="lg">Create an account to save your score</Button></Link>
        <Link href="/login?return_to=/trivia"><Button size="lg" variant="secondary">Log in to save your score</Button></Link>
      </div>
    </div>
  )
}

// ── Completion screen (member, all questions answered) ─────────────────────
function CompletionView({ stats }: { stats: Stats }) {
  return (
    <div className="max-w-xl mx-auto py-12 px-4 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-950/30 mb-6">
        <Trophy className="w-8 h-8 text-amber-500" />
      </div>
      <h1 className="mb-2">You've answered every question!</h1>
      <p className="text-stone-500 dark:text-stone-400 mb-6 leading-relaxed">
        You've worked through all {stats.total_questions} questions in the database.
        More will be added over time — check back soon.
      </p>
      <div className="rounded-xl border border-hairline bg-surface p-6 mb-8 text-left">
        <h2 className="text-base mb-4">Your final score</h2>
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-ink-muted">Questions answered</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.total_answered}</dd>
          <dt className="text-ink-muted">Correct answers</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.total_correct}</dd>
          <dt className="text-ink-muted">Score</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.percentage}%</dd>
        </dl>
      </div>
      <Link href="/leaderboard"><Button>See the leaderboard →</Button></Link>
    </div>
  )
}

// ── Member: round complete ───────────────────────────────────────────────────
function MemberRoundComplete({ questionCount, stats }: { questionCount: number; stats: Stats }) {
  return (
    <div className="max-w-xl mx-auto py-12 px-4 text-center">
      <CheckCircle2 className="w-12 h-12 text-amber-500 mx-auto mb-4" />
      <h1 className="mb-2">Round complete!</h1>
      <p className="text-stone-500 dark:text-stone-400 mb-6">
        You answered {questionCount} questions this round.
      </p>
      <div className="rounded-xl border border-hairline bg-surface p-6 mb-8 text-left">
        <h2 className="text-base mb-4">Running total</h2>
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-ink-muted">Answered so far</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.total_answered} / {stats.total_questions}</dd>
          <dt className="text-ink-muted">Correct</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.total_correct}</dd>
          <dt className="text-ink-muted">Score</dt>
          <dd className="font-medium text-ink-display tabular-nums">{stats.percentage}%</dd>
        </dl>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button onClick={() => router.get("/trivia")}>Next round →</Button>
        <Link href="/leaderboard"><Button variant="secondary">Leaderboard</Button></Link>
      </div>
    </div>
  )
}

// ── Shared active quiz round (guest + member) ────────────────────────────────
type QuizRoundProps = {
  questions:       Question[]
  initialStats:    Stats
  submitAnswer:    (question: Question, answerId: number) => Promise<AnswerResult>
  onRoundComplete: (collected: PendingAnswer[], finalStats: Stats) => ReactNode
}

function QuizRound({ questions, initialStats, submitAnswer, onRoundComplete }: QuizRoundProps) {
  const [current, setCurrent]     = useState(0)
  const [result, setResult]       = useState<AnswerResult | null>(null)
  const [stats, setStats]         = useState(initialStats)
  const [loading, setLoading]     = useState(false)
  const [roundDone, setRoundDone] = useState(false)
  const [collected, setCollected] = useState<PendingAnswer[]>([])

  const q = questions[current]

  const selectAnswer = async (answerId: number) => {
    if (result || loading) return
    setLoading(true)
    const data = await submitAnswer(q, answerId)
    setResult(data)
    setCollected(c => [ ...c, { question_id: q.id, answer_id: answerId } ])
    setStats(s => ({
      ...s,
      total_answered: s.total_answered + 1,
      total_correct:  s.total_correct + (data.correct ? 1 : 0),
      percentage:     parseFloat((((s.total_correct + (data.correct ? 1 : 0)) / (s.total_answered + 1)) * 100).toFixed(1)),
    }))
    setLoading(false)
  }

  const next = () => {
    if (current + 1 >= questions.length) {
      setRoundDone(true)
    } else {
      setCurrent(c => c + 1)
      setResult(null)
    }
  }

  if (roundDone) {
    return <>{onRoundComplete(collected, stats)}</>
  }

  return (
    <div className="max-w-xl mx-auto py-8 px-4">
      {/* Progress */}
      <div className="flex items-center justify-between mb-6 text-sm text-ink-muted">
        <span>Question {current + 1} of {questions.length}</span>
        <span>{stats.total_answered} answered · {stats.percentage}% correct</span>
      </div>
      <div className="w-full bg-stone-100 dark:bg-stone-800 rounded-full h-1.5 mb-8">
        <div
          className="bg-amber-400 h-1.5 rounded-full transition-all"
          style={{ width: `${((current + 1) / questions.length) * 100}%` }}
        />
      </div>

      {/* Question */}
      <h2 className="text-lg font-medium text-ink-display mb-6 leading-snug">{q.content}</h2>

      {/* Answers */}
      <div className="space-y-3 mb-6">
        {q.answers.map(a => {
          let className = "w-full text-left rounded-xl border px-4 py-3 text-sm transition-colors cursor-pointer "
          if (!result) {
            className += "border-hairline bg-surface hover:bg-stone-50 dark:hover:bg-stone-800"
          } else if (a.id === result.correct_answer_id) {
            className += "border-green-400 bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300 font-medium"
          } else if (!result.correct && a.id !== result.correct_answer_id) {
            className += "border-hairline bg-surface text-ink-muted"
          } else {
            className += "border-hairline bg-surface"
          }
          return (
            <button key={a.id} className={className} onClick={() => selectAnswer(a.id)} disabled={!!result || loading}>
              {a.content}
            </button>
          )
        })}
      </div>

      {/* Feedback */}
      {result && (
        <div className={`rounded-xl border p-4 mb-6 ${result.correct ? "border-green-300 bg-green-50 dark:bg-green-950/20" : "border-red-200 bg-red-50 dark:bg-red-950/20"}`}>
          <div className="flex items-center gap-2 mb-1">
            {result.correct
              ? <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
              : <XCircle className="w-4 h-4 text-red-500 dark:text-red-400" />
            }
            <span className={`text-sm font-medium ${result.correct ? "text-green-800 dark:text-green-300" : "text-red-700 dark:text-red-400"}`}>
              {result.correct ? "Correct!" : "Incorrect."}
            </span>
            {result.correct_rate !== null && (
              <span className="ml-auto text-xs text-ink-muted">
                {result.correct_rate}% of players got this right
                {result.difficulty && ` · ${result.difficulty}`}
              </span>
            )}
          </div>
          {result.more_info && (
            <p className="text-sm text-ink-body mt-1 leading-relaxed">{result.more_info}</p>
          )}
        </div>
      )}

      {result && (
        <Button onClick={next} className="w-full">
          {current + 1 >= questions.length ? "See round summary" : "Next question →"}
        </Button>
      )}
    </div>
  )
}

// ── Guest flow (intro → play → prompt to save) ──────────────────────────────
function GuestFlow({ heroImage, questions, stats }: { heroImage: string; questions: Question[]; stats: GuestStats }) {
  const [stage, setStage] = useState<"intro" | "playing">("intro")

  if (questions.length === 0) return <NoQuestionsYet />
  if (stage === "intro") return <GuestIntro heroImage={heroImage} stats={stats} onStart={() => setStage("playing")} />

  const initialStats: Stats = {
    total_answered:  0,
    total_correct:   0,
    percentage:      0,
    total_questions: stats.total_questions,
  }

  return (
    <QuizRound
      questions={questions}
      initialStats={initialStats}
      submitAnswer={(q, answerId) =>
        fetch("/quiz/guest_answer", {
          method:  "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
          body:    JSON.stringify({ question_id: q.id, answer_id: answerId }),
        }).then(r => r.json())
      }
      onRoundComplete={(collected, finalStats) => (
        <GuestQuizComplete stats={finalStats} collected={collected} />
      )}
    />
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Trivia({ hero_image, og_image, signed_in, completed, session_id, questions, stats }: Props) {
  return (
    <>
      <Head title="Trivia — Australian Music Database">
        <meta name="description" content="Test your knowledge of Australian music history. Work through 100+ trivia questions on bands, recordings, venues and chart history." />
        <meta property="og:title" content="Australian Music Trivia Quiz" />
        <meta property="og:description" content="Test your knowledge of Australian music history." />
        <meta property="og:image" content={og_image} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>
      <PublicShell>
        {!signed_in && <GuestFlow heroImage={hero_image} questions={questions} stats={stats as GuestStats} />}
        {signed_in && completed && <CompletionView stats={stats as Stats} />}
        {signed_in && !completed && session_id !== null && (
          <QuizRound
            questions={questions}
            initialStats={stats as Stats}
            submitAnswer={(q, answerId) =>
              fetch("/quiz/answer", {
                method:  "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
                body:    JSON.stringify({ session_id, question_id: q.id, answer_id: answerId }),
              }).then(r => r.json())
            }
            onRoundComplete={(_collected, finalStats) => (
              <MemberRoundComplete questionCount={questions.length} stats={finalStats} />
            )}
          />
        )}
      </PublicShell>
    </>
  )
}
